import { forwardRef, type CSSProperties, type KeyboardEvent, type ReactNode } from "react";

export type ChipTone =
  | "primary"
  | "accent"
  | "success"
  | "warning"
  | "danger"
  | "neutral";

/**
 * Chip — small pill/tag primitive (revamp 2026-05-11).
 *
 * Replaces `@nimbus-ds/components` `<Badge>` for inline text labels: Nimbus
 * 5.59 Badge stripped children content and rendered as an empty 20×24 pill
 * (the "blue blob everywhere" bug). This is a plain span styled via
 * `.chip` + `.chip--{tone}` CSS so it renders the text we actually pass it.
 *
 * For numeric counters / dot indicators, keep using Nimbus Badge with its
 * `count` / `type` props — those work correctly. Chip is for *text labels*.
 */
// L127 (Renata) — opened to accept `aria-label` + arbitrary `data-*`
// attributes so callers can stamp accessibility/QA hooks onto the pill
// without wrapping it in another span (extra DOM nodes drift styling).
// First consumer: `ChatInbox.tsx` citation pills, which now carry
// `aria-label="Fuente: <kind> — <label>"` + `data-cited-kind` +
// `data-cited-id` so the chat-citation linkback (L124 Inés-named
// output-dim gap) has both a screen-reader frame and a QA selector.
//
// L129 (Renata) — `onClick` opens the chip for interactive use without
// changing the rendered element to a `<button>` (button-reset CSS would
// destroy every `.chip` tone variant). When `onClick` is supplied we set
// `role="button"` + `tabIndex=0` + a keyboard handler for Enter/Space so
// the pill is fully operable via keyboard and announced as a button by
// screen readers. `aria-expanded` + `aria-haspopup` are caller-supplied
// (the chat citation popover uses both). Forwards a ref so the popover
// caller can return focus to the originating pill on dismiss — the
// accessibility floor for a click-to-open popover (WCAG 2.1.2).
export type ChipProps = {
  tone?: ChipTone;
  size?: "sm" | "md";
  className?: string;
  icon?: ReactNode;
  children: ReactNode;
  title?: string;
  style?: CSSProperties;
  onClick?: () => void;
  "aria-label"?: string;
  "aria-expanded"?: boolean;
  "aria-haspopup"?: "dialog" | "menu" | "listbox" | "tree" | "grid" | true;
  "aria-controls"?: string;
  "data-testid"?: string;
} & {
  [dataAttr: `data-${string}`]: string | number | undefined;
};

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(function Chip(
  {
    tone = "neutral",
    size = "md",
    className = "",
    icon,
    children,
    title,
    style,
    onClick,
    ...rest
  },
  ref,
) {
  const interactive = typeof onClick === "function";
  const handleKeyDown = interactive
    ? (event: KeyboardEvent<HTMLSpanElement>) => {
        // Enter + Space mirror native `<button>` activation. Space defaults
        // to scrolling the page on a non-button element; preventDefault
        // keeps the merchant's scroll position when they activate a chip
        // citation pill from the chat thread.
        if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
          event.preventDefault();
          onClick?.();
        }
      }
    : undefined;
  const interactiveClassName = interactive ? " chip--interactive" : "";
  return (
    <span
      ref={ref}
      className={`chip chip--${tone} chip--${size}${interactiveClassName} ${className}`.trim()}
      title={title}
      style={style}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      {...rest}
    >
      {icon ? <span className="chip__icon" aria-hidden="true">{icon}</span> : null}
      <span className="chip__label">{children}</span>
    </span>
  );
});
